import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { AuthState } from '@/contexts';
import { Permission, hasPermission } from '@/utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireOnyx?: boolean;
  requireAdmin?: boolean;
  requirePermission?: Permission;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireOnyx = false,
  requireAdmin = false,
  requirePermission,
}) => {
  const { state, user } = useAuth();
  const location = useLocation();

  if (state === AuthState.Initializing) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (state === AuthState.Unauthenticated || state === AuthState.TokenRefreshFailed) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Handle authentication error state
  if (state === AuthState.Error) {
    return <Navigate to="/auth-error" replace />;
  }

  // Check for ONYX user requirement
  if (requireOnyx && user && !user.isOnyxUser) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check for admin requirement
  if (requireAdmin && user && user.role !== 'admin') {
    return <Navigate to="/unauthorized" replace />;
  }

  // Check for specific permission requirement
  if (requirePermission && user && !hasPermission(user, requirePermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Render protected content
  return <>{children}</>;
};
